import { useState } from 'react'
import './APIDoc.css'

const CLI_COMMANDS = [
  {
    category: 'Sandbox',
    commands: [
      { cmd: 'harness new [name]', desc: 'Create a new sandbox' },
      { cmd: 'harness ls', desc: 'List sandboxes' },
      { cmd: 'harness use <name>', desc: 'Set current sandbox' },
      { cmd: 'harness current', desc: 'Show current sandbox' },
      { cmd: 'harness get [name]', desc: 'Show sandbox details' },
      { cmd: 'harness rm [name]', desc: 'Remove a sandbox' },
    ]
  },
  {
    category: 'Execution',
    commands: [
      { cmd: 'harness exec -- <command>', desc: 'Run a command' },
      { cmd: 'harness exec --pty -- <command>', desc: 'Run with PTY (interactive)' },
      { cmd: 'harness console [sandbox]', desc: 'Open interactive shell' },
    ]
  },
  {
    category: 'Snapshots',
    commands: [
      { cmd: 'harness commit [-m MSG]', desc: 'Save current state' },
      { cmd: 'harness log [target]', desc: 'Show commit history' },
    ]
  },
  {
    category: 'Branches',
    commands: [
      { cmd: 'harness branch <name>', desc: 'Create a new branch' },
      { cmd: 'harness branches', desc: 'List branches' },
      { cmd: 'harness checkout <target>', desc: 'Switch to branch or commit' },
    ]
  },
]

const ENDPOINTS = [
  {
    category: 'Sandbox',
    endpoints: [
      { method: 'POST', path: '/sandboxes', desc: 'Create a new sandbox' },
      { method: 'GET', path: '/sandboxes', desc: 'List all sandboxes' },
      { method: 'GET', path: '/sandboxes/{id}', desc: 'Get sandbox details' },
      { method: 'DELETE', path: '/sandboxes/{id}', desc: 'Destroy sandbox' },
    ]
  },
  {
    category: 'Execution',
    endpoints: [
      { method: 'POST', path: '/sandboxes/{id}/exec', desc: 'Execute command' },
      { method: 'GET', path: '/sandboxes/{id}/exec/ws', desc: 'WebSocket PTY session' },
    ]
  },
  {
    category: 'Snapshots',
    endpoints: [
      { method: 'POST', path: '/sandboxes/{id}/snapshots', desc: 'Create snapshot' },
      { method: 'GET', path: '/sandboxes/{id}/snapshots', desc: 'List snapshots' },
      { method: 'GET', path: '/sandboxes/{id}/snapshots/tasks/{task_id}', desc: 'Get async task status' },
      { method: 'DELETE', path: '/sandboxes/{id}/snapshots/{snapshot_id}', desc: 'Delete snapshot' },
    ]
  },
  {
    category: 'Branches',
    endpoints: [
      { method: 'POST', path: '/sandboxes/{id}/branches', desc: 'Create branch' },
      { method: 'GET', path: '/sandboxes/{id}/branches', desc: 'List branches' },
      { method: 'GET', path: '/sandboxes/{id}/branches/{name}', desc: 'Get branch' },
      { method: 'POST', path: '/sandboxes/{id}/branch', desc: 'Checkout (restore)' },
    ]
  },
]

const EXAMPLE_CREATE = `POST /sandboxes
Authorization: Bearer <token>
Content-Type: application/json

{
  "name": "my-sandbox"
}`

const EXAMPLE_CREATE_RESPONSE = `201 Created

{
  "id": "550e8400-e29b-41d4-a716-446655440000",
  "name": "my-sandbox",
  "status": "ready",
  "head_snapshot_id": "...",
  "head_ref_kind": "branch",
  "head_ref": "main"
}`

const EXAMPLE_EXEC = `POST /sandboxes/my-sandbox/exec
Authorization: Bearer <token>
Content-Type: application/json

{
  "command": ["bash", "-c", "echo hello"],
  "timeout_secs": 30,
  "user": "harness"
}`

const EXAMPLE_EXEC_RESPONSE = `200 OK

{
  "exit_code": 0,
  "stdout": "hello\\n",
  "stderr": ""
}`

const EXAMPLE_SNAPSHOT = `POST /sandboxes/my-sandbox/snapshots
Authorization: Bearer <token>
Content-Type: application/json

{
  "name": "checkpoint-1"
}`

const EXAMPLE_SNAPSHOT_RESPONSE = `200 OK

{
  "id": "660e8400-e29b-41d4-a716-446655440001",
  "sandbox_id": "550e8400-...",
  "parent_id": null,
  "name": "checkpoint-1",
  "created_at": "2026-01-29T12:00:00Z"
}`

const EXAMPLE_CHECKOUT = `POST /sandboxes/my-sandbox/branch
Authorization: Bearer <token>
Content-Type: application/json

{
  "snapshot_id": "660e8400-...",
  "name": "feature-branch"
}`

const EXAMPLE_CHECKOUT_RESPONSE = `200 OK

{
  "id": "550e8400-...",
  "name": "my-sandbox",
  "status": "ready",
  "head_ref_kind": "branch",
  "head_ref": "feature-branch"
}`

const CLI_EXAMPLE = `# Create and use a sandbox
$ harness new my-sandbox
$ harness use my-sandbox

# Run commands
$ harness exec -- echo "hello world"
hello world

# Save state and create branch
$ harness commit -m "initial state"
$ harness branch feature-a

# Make changes and commit
$ harness exec -- touch /tmp/test.txt
$ harness commit -m "added test file"

# Switch branches
$ harness checkout main
$ harness checkout feature-a`

export function APIDoc() {
  const [activeTab, setActiveTab] = useState<'cli' | 'rest'>('cli')

  return (
    <section className="api-doc">
      <div className="api-doc__content">
        <div className="api-doc__header">
          <span className="api-doc__tag">REFERENCE</span>
          <div className="api-doc__tabs">
            <button
              className={`api-doc__tab ${activeTab === 'cli' ? 'api-doc__tab--active' : ''}`}
              onClick={() => setActiveTab('cli')}
            >
              [CLI]
            </button>
            <button
              className={`api-doc__tab ${activeTab === 'rest' ? 'api-doc__tab--active' : ''}`}
              onClick={() => setActiveTab('rest')}
            >
              [REST API]
            </button>
          </div>
        </div>

        {activeTab === 'cli' ? (
          <>
            <div className="api-doc__intro">
              <p>Install: <code>curl -fsSL https://harness.tonbo.dev/install.sh | sh</code></p>
            </div>

            <div className="api-doc__endpoints">
              {CLI_COMMANDS.map((category) => (
                <div key={category.category} className="api-doc__category">
                  <h3 className="api-doc__category-title">{category.category}</h3>
                  <table className="api-doc__table">
                    <tbody>
                      {category.commands.map((cmd) => (
                        <tr key={cmd.cmd}>
                          <td className="api-doc__cmd">{cmd.cmd}</td>
                          <td className="api-doc__desc">{cmd.desc}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))}
            </div>

            <div className="api-doc__examples">
              <h3 className="api-doc__examples-title">Example Session</h3>
              <pre className="api-doc__code api-doc__code--cli">{CLI_EXAMPLE}</pre>
            </div>
          </>
        ) : (
          <>
            <div className="api-doc__intro">
              <p>Base URL: <code>https://api.harness.dev</code></p>
              <p>Authentication: <code>Authorization: Bearer &lt;token&gt;</code></p>
            </div>

            <div className="api-doc__endpoints">
              {ENDPOINTS.map((category) => (
                <div key={category.category} className="api-doc__category">
                  <h3 className="api-doc__category-title">{category.category}</h3>
                  <table className="api-doc__table">
                    <tbody>
                      {category.endpoints.map((ep) => (
                        <tr key={ep.path + ep.method}>
                          <td className={`api-doc__method api-doc__method--${ep.method.toLowerCase()}`}>
                            {ep.method}
                          </td>
                          <td className="api-doc__path">{ep.path}</td>
                          <td className="api-doc__desc">{ep.desc}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))}
            </div>

            <div className="api-doc__examples">
              <h3 className="api-doc__examples-title">Examples</h3>

              <div className="api-doc__example">
                <h4>Create Sandbox</h4>
                <div className="api-doc__example-blocks">
                  <pre className="api-doc__code api-doc__code--request">{EXAMPLE_CREATE}</pre>
                  <pre className="api-doc__code api-doc__code--response">{EXAMPLE_CREATE_RESPONSE}</pre>
                </div>
              </div>

              <div className="api-doc__example">
                <h4>Execute Command</h4>
                <div className="api-doc__example-blocks">
                  <pre className="api-doc__code api-doc__code--request">{EXAMPLE_EXEC}</pre>
                  <pre className="api-doc__code api-doc__code--response">{EXAMPLE_EXEC_RESPONSE}</pre>
                </div>
              </div>

              <div className="api-doc__example">
                <h4>Create Snapshot</h4>
                <div className="api-doc__example-blocks">
                  <pre className="api-doc__code api-doc__code--request">{EXAMPLE_SNAPSHOT}</pre>
                  <pre className="api-doc__code api-doc__code--response">{EXAMPLE_SNAPSHOT_RESPONSE}</pre>
                </div>
              </div>

              <div className="api-doc__example">
                <h4>Checkout Branch</h4>
                <div className="api-doc__example-blocks">
                  <pre className="api-doc__code api-doc__code--request">{EXAMPLE_CHECKOUT}</pre>
                  <pre className="api-doc__code api-doc__code--response">{EXAMPLE_CHECKOUT_RESPONSE}</pre>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </section>
  )
}
